import { Fragment, useMemo, useState, useEffect, useRef } from "react";
import ModalNew from "../popup/Modal";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import { PopupHeader, PopupContent } from "./common";
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker using local file from public folder
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

function DisplayDocument({
  documentName,
  documentFile,
  mimeType,
  isLoadingDocument,
  hideShowDocument,
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [renderingPdf, setRenderingPdf] = useState(false);
  const [pdfZoom, setPdfZoom] = useState(1.0);
  const canvasContainerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ left: 0, top: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Check if the file is a CSV
  const isCSV = useMemo(() => {
    if (!mimeType) return false;
    const csvMimeTypes = ["text/csv", "text/plain", "application/csv", "application/vnd.ms-excel"];
    const lowerMimeType = mimeType.toLowerCase();
    const isCSVMimeType = csvMimeTypes.some(type => lowerMimeType.includes(type));
    const isCSVExtension = documentName?.toLowerCase().endsWith(".csv");
    return isCSVMimeType || isCSVExtension;
  }, [mimeType, documentName]);

  // Decode base64 to text for CSV files
  const csvContent = useMemo(() => {
    if (!isCSV || !documentFile) return "";
    try {
      return atob(documentFile);
    } catch (e) {
      console.error("Failed to decode CSV content:", e);
      return "";
    }
  }, [isCSV, documentFile]);

  // Function to render PDF pages
  const renderPdfPages = async (pdf, zoom) => {
    const container = canvasContainerRef.current;
    
    if (!container) {
      return;
    }

    // Clear previous canvases safely
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // Render all pages
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      
      // Calculate display size to fit container width
      const containerWidth = container.offsetWidth || 700;
      const pageViewport = page.getViewport({ scale: 1 });
      const displayScale = (containerWidth / pageViewport.width) * zoom;
      
      // Render at high resolution (2x or higher for crisp display)
      const pixelRatio = window.devicePixelRatio || 1;
      const renderScale = displayScale * Math.max(pixelRatio, 2);
      const viewport = page.getViewport({ scale: renderScale });

      const canvas = document.createElement('canvas');
      canvas.style.display = 'block';
      canvas.style.margin = '10px auto';
      canvas.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
      
      // Set display size using CSS (scaled down for container)
      canvas.style.width = `${pageViewport.width * displayScale}px`;
      canvas.style.height = `${pageViewport.height * displayScale}px`;
      
      // Set actual canvas size (high resolution)
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      container.appendChild(canvas);
    }
  };

  // Render PDF using PDF.js or create blob URL for images
  useEffect(() => {
    if (!documentFile || isCSV || isLoadingDocument) {
      setBlobUrl(null);
      setRenderingPdf(false);
      return;
    }

    const isPDF = mimeType === "application/pdf" || documentName?.toLowerCase().endsWith('.pdf');

    try {
      const byteCharacters = atob(documentFile);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      if (isPDF) {
        // Use PDF.js for PDF files
        setRenderingPdf(true);
        setBlobUrl(null);

        const loadingTask = pdfjsLib.getDocument({ data: byteArray });
        
        loadingTask.promise.then(async (pdf) => {
          // Store PDF reference for zoom functionality
          pdfDocRef.current = pdf;
          
          // First set rendering to false to remove the loading placeholder
          setRenderingPdf(false);
          
          // Wait for next frame to ensure React has updated
          await new Promise(resolve => requestAnimationFrame(resolve));
          
          renderPdfPages(pdf, pdfZoom);
        }).catch((error) => {
          setRenderingPdf(false);
          // Fallback to blob URL if PDF.js fails
          const blob = new Blob([byteArray], { type: mimeType || "application/pdf" });
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
        });
      } else {
        // Use blob URL for non-PDF files (images)
        setRenderingPdf(false);
        const blob = new Blob([byteArray], { type: mimeType || "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);

        return () => {
          URL.revokeObjectURL(url);
        };
      }
    } catch (e) {
      setBlobUrl(null);
      setRenderingPdf(false);
    }
  }, [documentFile, mimeType, isCSV, isLoadingDocument, documentName]);

  // Re-render PDF when zoom or fullscreen changes
  useEffect(() => {
    if (pdfDocRef.current && pdfZoom && canvasContainerRef.current) {
      // Wait for container to resize before re-rendering
      // Use setTimeout to allow CSS changes to apply and browser to reflow
      const timeoutId = setTimeout(() => {
        if (canvasContainerRef.current) {
          renderPdfPages(pdfDocRef.current, pdfZoom);
        }
      }, 150);
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfZoom, isFullscreen]);

  // Inject CSS to hide scrollbars for webkit browsers
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .pdf-container-no-scrollbar::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // ESC key to exit fullscreen
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isFullscreen]);

  // Drag-to-pan handlers
  const handleMouseDown = (e) => {
    const container = canvasContainerRef.current;
    if (!container) return;
    
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setScrollStart({ left: container.scrollLeft, top: container.scrollTop });
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const container = canvasContainerRef.current;
    if (!container) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    container.scrollLeft = scrollStart.left - deltaX;
    container.scrollTop = scrollStart.top - deltaY;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Handle download
  const handleDownload = () => {
    if (!documentFile) return;
    const byteCharacters = atob(documentFile);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = documentName || "document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const hasValidDocument = documentFile && documentFile.length > 0;

  return (
    <Fragment>
      <ModalNew 
        className={`content-modal ${isFullscreen ? 'fullscreen-modal' : ''}`}
        style={{ 
          maxWidth: isFullscreen ? "100vw" : "800px",
          width: isFullscreen ? "100vw" : undefined,
          height: isFullscreen ? "100vh" : undefined,
          margin: isFullscreen ? "0" : undefined,
          maxHeight: isFullscreen ? "100vh" : undefined,
          borderRadius: isFullscreen ? "0" : undefined,
          left: isFullscreen ? "0" : undefined,
          transform: isFullscreen ? "none" : undefined,
        }}
      >
        <PopupHeader
          title={documentName || "Document"}
          setOpenState={(isOpen) => { if (!isOpen) hideShowDocument(); }}
          icon={`${process.env.PUBLIC_URL}/images/icons/doc_blue_popup.svg`}
          variant="compact"
        />
        <PopupContent 
          maxHeight={isFullscreen ? "calc(100vh - 80px)" : "70vh"} 
          padding="20px"
        >
          <div
            id="documentViewer"
            style={{
              minHeight: "400px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: isLoadingDocument ? "center" : "flex-start",
            }}
          >
            {isLoadingDocument && <LoadingPlaceholder rows={1} type="card-large" />}
            
            {!isLoadingDocument && !hasValidDocument && (
              <div style={{ 
                textAlign: "center", 
                padding: "40px", 
                color: "#666" 
              }}>
                <p>Unable to display document. Please try downloading it instead.</p>
              </div>
            )}

            {!isLoadingDocument && hasValidDocument && isCSV && (
              <div style={{ width: "100%" }}>
                <pre
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "16px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    overflow: "auto",
                    maxHeight: "500px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    fontFamily: "monospace",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  {csvContent}
                </pre>
              </div>
            )}

            {/* Canvas container for rendered PDF pages - always render for PDFs */}
            {!isLoadingDocument && hasValidDocument && !isCSV && (mimeType === "application/pdf" || documentName?.toLowerCase().endsWith('.pdf')) && (
              <>
                {/* Zoom controls */}
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  gap: "12px",
                  marginBottom: "12px",
                  padding: "8px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px"
                }}>
                  <button
                    onClick={() => setPdfZoom(Math.max(0.5, pdfZoom - 0.25))}
                    disabled={pdfZoom <= 0.5}
                    className="cta-button secondary"
                    style={{ padding: "6px 12px", fontSize: "14px" }}
                  >
                    −
                  </button>
                  <span style={{ fontSize: "14px", minWidth: "60px", textAlign: "center" }}>
                    {Math.round(pdfZoom * 100)}%
                  </span>
                  <button
                    onClick={() => setPdfZoom(Math.min(3.0, pdfZoom + 0.25))}
                    disabled={pdfZoom >= 3.0}
                    className="cta-button secondary"
                    style={{ padding: "6px 12px", fontSize: "14px" }}
                  >
                    +
                  </button>
                  <button
                    onClick={() => setPdfZoom(1.0)}
                    className="cta-button secondary"
                    style={{ padding: "6px 12px", fontSize: "12px" }}
                  >
                    Reset
                  </button>
                  <div style={{ width: "1px", height: "24px", backgroundColor: "#ccc", margin: "0 4px" }}></div>
                  <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className="cta-button secondary"
                    style={{ padding: "6px 12px", fontSize: "16px" }}
                    title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  >
                    {isFullscreen ? "⤓" : "⤢"}
                  </button>
                </div>

                <div 
                  ref={canvasContainerRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                  style={{ 
                    width: "100%", 
                    maxHeight: isFullscreen ? "calc(100vh - 140px)" : "600px",
                    overflow: "auto",
                    backgroundColor: "#525659",
                    minHeight: isFullscreen ? "calc(100vh - 140px)" : "400px",
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none', // Prevent text selection while dragging
                    // Hide scrollbars
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE/Edge
                  }}
                  className="pdf-container-no-scrollbar"
                >
                  {renderingPdf && (
                    <div style={{ 
                      textAlign: "center",
                      padding: "40px",
                      color: "white"
                    }}>
                      <LoadingPlaceholder rows={1} type="card-large" />
                      <p style={{ marginTop: "10px" }}>Rendering PDF...</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Image display using blob URL */}
            {!isLoadingDocument && hasValidDocument && !isCSV && blobUrl && (mimeType !== "application/pdf" && !documentName?.toLowerCase().endsWith('.pdf')) && (
              <div style={{ width: "100%", maxHeight: "600px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <img 
                  src={blobUrl} 
                  alt={documentName || "Document"}
                  style={{ 
                    maxWidth: "100%", 
                    maxHeight: "600px",
                    objectFit: "contain"
                  }}
                />
              </div>
            )}
          </div>

          <div 
            className="modal-actions" 
            style={{ 
              display: "flex", 
              gap: "12px", 
              justifyContent: "center",
              marginTop: "20px",
              paddingTop: "16px",
              borderTop: "1px solid #e0e0e0"
            }}
          >
            <button className="cta-button secondary" onClick={handleDownload}>
              Download
            </button>
            <button className="cta-button primary" onClick={hideShowDocument}>
              Close
            </button>
          </div>
        </PopupContent>
      </ModalNew>
    </Fragment>
  );
}

export default DisplayDocument;
